// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API_BASE } from "../config";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // ✅ Refresh user from backend
  // accepts optional explicit token so it works immediately after login
  const refreshUser = useCallback(async (overrideToken) => {
    const authToken = overrideToken || token;
    if (!authToken) return null;

    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
        cache: "no-store",
      });

      if (!res.ok) {
        if (res.status === 401) {
          setUser(null);
          setToken(null);
          localStorage.removeItem("user");
          localStorage.removeItem("token");
        }
        return null;
      }

      const ct = res.headers.get("content-type") || "";
      const text = await res.text();
      const fresh = ct.includes("application/json") && text ? JSON.parse(text) : null;

      const freshUser = fresh?.user ?? fresh;

      if (!freshUser) return null;

      setUser(freshUser);
      localStorage.setItem("user", JSON.stringify(freshUser));
      return freshUser;
    } catch (err) {
      console.error("[refreshUser] failed:", err);
      return null;
    }
  }, [token]);

  // ✅ Restore login after refresh
  // IMPORTANT: restore token first, then fetch real user from backend
  useEffect(() => {
    let mounted = true;

    const restoreAuth = async () => {
      try {
        const savedToken = localStorage.getItem("token");

        if (!savedToken) {
          if (mounted) setLoadingAuth(false);
          return;
        }

        if (mounted) {
          setToken(savedToken);
          setUser(null); // prevent stale previous-user flash
        }

        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${savedToken}` },
          cache: "no-store",
        });

        if (!res.ok) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (mounted) {
            setToken(null);
            setUser(null);
          }
          return;
        }

        const ct = res.headers.get("content-type") || "";
        const text = await res.text();
        const fresh = ct.includes("application/json") && text ? JSON.parse(text) : null;
        const freshUser = fresh?.user ?? fresh;

        if (mounted) {
          setUser(freshUser || null);
        }

        if (freshUser) {
          localStorage.setItem("user", JSON.stringify(freshUser));
        } else {
          localStorage.removeItem("user");
        }
      } catch (e) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (mounted) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (mounted) setLoadingAuth(false);
      }
    };

    restoreAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // ✅ REAL login (username + password)
  const login = async (username, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
      cache: "no-store",
    });

    const ct = res.headers.get("content-type") || "";
    const text = await res.text();
    const data = ct.includes("application/json") && text ? JSON.parse(text) : null;

    if (!res.ok) throw new Error(data?.error || text || "Login failed");

    // ✅ clear previous user immediately
    setUser(null);

    // ✅ save token first
    setToken(data.token);
    localStorage.setItem("token", data.token);

    // optional temporary save, but real source of truth is refreshUser()
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
    } else {
      localStorage.removeItem("user");
    }

    // ✅ fetch real current user using the new token
    const freshUser = await refreshUser(data.token);

    // fallback if backend already returned user but /me didn't
    const finalUser = freshUser || data.user || null;

    setUser(finalUser);

    if (finalUser) {
      localStorage.setItem("user", JSON.stringify(finalUser));
    } else {
      localStorage.removeItem("user");
    }

    return finalUser;
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  }, []);

  // ✅ authFetch
  const authFetch = useCallback(
    async (url, options = {}) => {
      const method = (options.method || "GET").toUpperCase();

      const headers = {
        ...(options.headers || {}),
      };

      const hasBody = options.body !== undefined && options.body !== null;
      const isFormData = hasBody && options.body instanceof FormData;

      if (hasBody && !isFormData && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const fetchOptions = { ...options, method, headers };

      if (method === "GET" && fetchOptions.cache == null) {
        fetchOptions.cache = "no-store";
        headers["Cache-Control"] = headers["Cache-Control"] || "no-cache";
        headers.Pragma = headers.Pragma || "no-cache";
      }

      const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;
      const res = await fetch(fullUrl, fetchOptions);

      if (res.status === 401) {
        console.log("[authFetch] 401 -> logout()");
        logout();
      } else if (res.status === 403) {
        console.log("[authFetch] 403 -> Forbidden (no logout)");
      }

      return res;
    },
    [token, logout]
  );

  return (
    <AuthContext.Provider
      value={{ user, token, login, logout, authFetch, refreshUser, loadingAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}